import { FormControlStatus } from '@angular/forms';
import { ReportFormRuleTemplate, RuleContext } from '@navstation-portal/reporting/data-model/ui';
import {
  FormState,
  ReportTabRulesKey,
  isReportTabRulesKey,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { evaluate } from '@navstation-portal/reporting/util/form-rules';
import { isFieldOnScreen } from './form-rule-lookup.utility';
import { isEmptyValue } from './form-validity.utility';
import { isStateField } from './vm-builder-helpers';

/** One field of one tab, and the status the store writes for it. */
export interface StaleFieldStatus {
  tabId: ReportTabRulesKey;
  sectionId: string;
  fieldId: string;
  status: FormControlStatus;
}

/**
 * Finds each field whose stored status says INVALID while the form asks nothing
 * of it, and gives the status the store writes in its place.
 *
 * Two cases, and both come from the same cause. A field holds its last status,
 * and the only thing that stamps a status before a tab renders is
 * `validateField`, which still reads the form-field-rules payload. The payload
 * and the template disagree, so a field can carry an INVALID that no rule and
 * no form control agrees with, and the tab then reports invalid with nothing on
 * screen to explain why.
 *
 * - **The template hides the field.** It writes DISABLED, which is what a
 *   control reports when it leaves the form, and which every tab reads as
 *   valid. A field that never renders has no control to report it itself.
 * - **The template does not require the field, and the field is empty.** It
 *   writes VALID. An empty field has nothing else to be invalid about: a
 *   comparison such as a minimum date needs a value to compare, so a field with
 *   no value and no requirement cannot hold a real error. A field that holds a
 *   value is left alone, whatever its status, because its INVALID can be a real
 *   one.
 *
 * The visibility check is the one that the store uses, so the two cannot
 * disagree.
 *
 * An array-of-rows section is left alone. The template holds one rule for the
 * section and none for a row, so nothing here can speak for a row.
 */
export const findStaleInvalidFields = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  formStateByTab: Partial<Record<ReportTabRulesKey, FormState>>
): StaleFieldStatus[] => {
  const stale: StaleFieldStatus[] = [];

  for (const [tabId, tab] of Object.entries(template?.tabs ?? {})) {
    // A template tab that the form does not have holds no state to correct.
    if (!isReportTabRulesKey(tabId)) continue;
    const formState = formStateByTab[tabId];
    if (!formState) continue;

    for (const [sectionId, section] of Object.entries(tab.sections ?? {})) {
      for (const [fieldId, field] of Object.entries(section.fields ?? {})) {
        const entry = formState[sectionId]?.[fieldId];
        if (!isStateField(entry) || entry.status !== 'INVALID') continue;

        if (!isFieldOnScreen(template, context, tabId, sectionId, fieldId)) {
          stale.push({ tabId, sectionId, fieldId, status: 'DISABLED' });
          continue;
        }

        const isRequired = field.isFieldRequired ? evaluate(field.isFieldRequired, context) : false;
        if (!isRequired && isEmptyValue(entry.value)) {
          stale.push({ tabId, sectionId, fieldId, status: 'VALID' });
        }
      }
    }
  }

  return stale;
};
